import { getAssetUrl } from '@shared/constants';
import {
  SOUNDS,
  SoundCategory,
  POSITIONAL_CATEGORIES,
  SOUND_CATEGORY_ORDER,
  type SoundDef,
} from '@shared/data/sounds';

/**
 * Motor de audio de Aureum (WebAudio, sin dependencias). Fuente de verdad de la reproducción:
 *
 *  - **Buses**: un GainNode por categoría (`SoundCategory`) → (buses posicionales pasan por el
 *    filtro "muerte") → master GainNode → duck GainNode (silencio) → destination. Cada categoría
 *    tiene volumen + toggle independientes; el master agrupa a todas.
 *  - **3D posicional**: las categorías en `POSITIONAL_CATEGORIES` (pasos/combate/hechizos/npc/
 *    entorno) se atenúan y panean según la distancia entre el emisor (tile) y el oyente (jugador
 *    local, centro = 100%). Alcance = medio-viewport + 10 tiles; al borde se oye muy bajito.
 *  - **Música**: una sola pista a la vez con crossfade; `setMusicRate` la acelera (vida baja).
 *  - **Loops**: heartbeat + ruidos de entorno gestionados por id (`startLoop`/`stopLoop`).
 *  - **Filtro muerte**: un BiquadFilter low-pass delante de los buses posicionales; al morir el
 *    entorno se oye "en burbuja" (barato, se togglea con el estado muerto).
 *  - **Silencio**: `fadeAll` hace fade-out/in del duck global (espeja el FX de silencio).
 *
 * Carga **lazy** por sonido (fetch + decodeAudioData, cacheado), fuera del bundle boot. Un `play`
 * de un id sin archivo cargado es un **no-op silencioso**: el sistema no rompe sin assets. Ver
 * docs/SOUND_AND_MUSIC.md.
 */

export interface AudioCategoryState {
  enabled: boolean;
  volume: number; // 0..1
}

export interface AudioConfig {
  masterVolume: number; // 0..1
  categories: Record<SoundCategory, AudioCategoryState>;
  /** Si la música se acelera con vida baja (<30% HP → 1.35x). Default true. */
  musicLowHpSpeedup: boolean;
}

export interface PlayOptions {
  /** Tile del emisor (para posicional). Si falta, se reproduce 2D. */
  tileX?: number;
  tileY?: number;
  /** playbackRate (1 = normal). */
  rate?: number;
  /** Multiplicador de volumen extra (0..1) sobre el volumen base del sonido. */
  volume?: number;
  /** Forzar loop (default: el `loop` del SoundDef). */
  loop?: boolean;
  /**
   * Índice de variante a reproducir (mod nº de variantes). Si viene, la elección es DETERMINISTA
   * (misma variante en todos los clientes) en vez de aleatoria. Ver footsteps.
   */
  variantIndex?: number;
  /** Callback al terminar (no dispara en loops hasta stop). */
  onEnd?: () => void;
}

/** Handle de una voz en reproducción. */
export interface SoundHandle {
  stop: (fadeMs?: number) => void;
}

const DEFAULT_MASTER_VOLUME = 0.8;
const DEATH_FILTER_FREQ_OPEN = 20000; // pass-through
const DEATH_FILTER_FREQ_MUFFLED = 480; // "burbuja"
// Reverb de "cueva" (envío wet colgado del bus posicional). El mix wet máx (a intensidad 1) y la
// forma del IR procedural (cola tipo cueva). La intensidad por zona (0..1) escala hasta REVERB_WET_MAX.
const REVERB_WET_MAX = 0.9;
const REVERB_IR_SECONDS = 2.5;
const REVERB_IR_DECAY = 2.5;
/** Margen (en tiles) más allá del viewport visible donde todavía se oye (muy bajito). */
const AUDIBLE_MARGIN_TILES = 10;
/** Ganancia mínima audible en el borde del alcance. */
const EDGE_GAIN = 0.05;

export function defaultAudioConfig(): AudioConfig {
  const categories = {} as Record<SoundCategory, AudioCategoryState>;
  for (const cat of SOUND_CATEGORY_ORDER) {
    categories[cat] = { enabled: true, volume: 1 };
  }
  return { masterVolume: DEFAULT_MASTER_VOLUME, categories, musicLowHpSpeedup: true };
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain!: GainNode;
  private duckGain!: GainNode;
  private deathFilter!: BiquadFilterNode;
  private convolver!: ConvolverNode;
  private reverbWet!: GainNode;
  private reverbTarget = 0;
  private busGains = new Map<SoundCategory, GainNode>();

  private config: AudioConfig = defaultAudioConfig();

  /** Buffers decodificados por PATH concreto (sin extensión). */
  private buffers = new Map<string, AudioBuffer>();
  /** Paths que fallaron al cargar (archivo ausente) → no reintentar, no-op. */
  private missing = new Set<string>();
  /** Cargas en vuelo (dedup). */
  private loading = new Map<string, Promise<AudioBuffer | null>>();

  // Oyente (jugador local) + viewport en tiles (los setea GameScene por frame).
  private listenerX = 0;
  private listenerY = 0;
  private viewHalfX = 15;
  private viewHalfY = 10;

  // Música
  private musicId: string | null = null;
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private musicRate = 1;

  // Loops gestionados por id (heartbeat, ambiente)
  private loops = new Map<
    string,
    {
      source: AudioBufferSourceNode;
      gain: GainNode;
      /** Panner si el loop es posicional (se actualiza con `updateLoopSpatial`). */
      panner?: StereoPannerNode;
      /** Volumen base del loop (para reescalar por la ganancia espacial). */
      baseVolume: number;
      /** Tile del emisor si es posicional. */
      tileX?: number;
      tileY?: number;
    }
  >();

  private deathMuffled = false;

  // ==========================================================================
  // Ciclo de vida
  // ==========================================================================

  /**
   * Crea/reanuda el AudioContext. `resume()` sólo prospera dentro de un gesto de usuario (política de
   * autoplay del browser). Devuelve una promesa que resuelve cuando el estado se estabilizó, así el
   * caller puede consultar `isRunning()` para saber si realmente quedó sonando o sigue bloqueado.
   */
  public async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.buildGraph();
      this.applyConfig(this.config);
    }
    if (this.ctx.state === 'suspended') {
      // `resume()` sin activación del usuario es rechazado por el browser Y loguea un warning ruidoso
      // ("The AudioContext was not allowed to start..."). Sólo lo intentamos si hubo un gesto (o si la
      // API `userActivation` no existe → fallback a intentarlo). Para usuarios con autoplay permitido
      // (MEI/PWA) el ctx ya nace 'running' y no llega acá. En el resto, el resume prospera en el gesto.
      const hasActivation = navigator.userActivation?.hasBeenActive ?? true;
      if (hasActivation) {
        try {
          await this.ctx.resume();
        } catch {
          /* aún bloqueado: queda suspendido, se reintenta en el próximo gesto */
        }
      }
    }
  }

  /** `true` sólo si el AudioContext existe y está efectivamente sonando (no suspendido). */
  public isRunning(): boolean {
    return this.ctx?.state === 'running';
  }

  private buildGraph(): void {
    const ctx = this.ctx!;
    this.duckGain = ctx.createGain();
    this.duckGain.gain.value = 1;
    this.duckGain.connect(ctx.destination);

    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = this.config.masterVolume;
    this.masterGain.connect(this.duckGain);

    // Filtro "muerte": low-pass delante de los buses posicionales.
    this.deathFilter = ctx.createBiquadFilter();
    this.deathFilter.type = 'lowpass';
    this.deathFilter.frequency.value = DEATH_FILTER_FREQ_OPEN;
    this.deathFilter.Q.value = 0.7;
    this.deathFilter.connect(this.masterGain);

    // Reverb de "cueva": envío WET en paralelo colgado del bus posicional (deathFilter) →
    // convolver → reverbWet → master. Sólo reverbera el sonido del MUNDO (no UI/música/heartbeat,
    // que van directo a master). El mix wet (0 = sin reverb) lo setea `setReverb` por zona. El IR es
    // procedural (sin asset). Ver docs/SOUND_AND_MUSIC.md.
    this.convolver = ctx.createConvolver();
    this.convolver.buffer = this.makeImpulseResponse(REVERB_IR_SECONDS, REVERB_IR_DECAY);
    this.reverbWet = ctx.createGain();
    this.reverbWet.gain.value = 0;
    this.reverbTarget = 0;
    this.deathFilter.connect(this.convolver);
    this.convolver.connect(this.reverbWet);
    this.reverbWet.connect(this.masterGain);

    for (const cat of SOUND_CATEGORY_ORDER) {
      const g = ctx.createGain();
      const state = this.config.categories[cat];
      g.gain.value = state && state.enabled ? state.volume : 0;
      // Posicionales → filtro muerte; el resto → master directo.
      g.connect(POSITIONAL_CATEGORIES.has(cat) ? this.deathFilter : this.masterGain);
      this.busGains.set(cat, g);
    }
  }

  public get isReady(): boolean {
    return this.ctx !== null;
  }

  // ==========================================================================
  // Config (buses)
  // ==========================================================================

  public applyConfig(config: AudioConfig): void {
    this.config = config;
    if (!this.ctx) return;
    this.masterGain.gain.value = config.masterVolume;
    for (const cat of SOUND_CATEGORY_ORDER) {
      const g = this.busGains.get(cat);
      const state = config.categories[cat];
      if (g && state) g.gain.value = state.enabled ? state.volume : 0;
    }
  }

  public getConfig(): AudioConfig {
    return this.config;
  }

  public setMasterVolume(v: number): void {
    this.config.masterVolume = clamp01(v);
    if (this.ctx) this.masterGain.gain.value = this.config.masterVolume;
  }

  public setCategoryVolume(cat: SoundCategory, v: number): void {
    const state = this.config.categories[cat];
    if (!state) return;
    state.volume = clamp01(v);
    this.refreshBus(cat);
  }

  public setCategoryEnabled(cat: SoundCategory, enabled: boolean): void {
    const state = this.config.categories[cat];
    if (!state) return;
    state.enabled = enabled;
    this.refreshBus(cat);
    // Si se apagó heartbeat/ambiente, cortar sus loops en vivo.
    if (!enabled) {
      for (const [id, loop] of this.loops) {
        if (SOUNDS[id]?.category === cat) {
          this.stopLoopInternal(id, loop);
        }
      }
      if (cat === SoundCategory.MUSIC) this.stopMusic(200);
    }
  }

  private refreshBus(cat: SoundCategory): void {
    const g = this.busGains.get(cat);
    const state = this.config.categories[cat];
    if (g && state) g.gain.value = state.enabled ? state.volume : 0;
  }

  // ==========================================================================
  // Oyente / viewport (los actualiza GameScene por frame)
  // ==========================================================================

  public setListener(tileX: number, tileY: number): void {
    this.listenerX = tileX;
    this.listenerY = tileY;
  }

  public setViewportHalfTiles(halfX: number, halfY: number): void {
    this.viewHalfX = halfX;
    this.viewHalfY = halfY;
  }

  /**
   * Ganancia + paneo de un emisor en (tileX,tileY) respecto del oyente. `null` si está fuera de
   * alcance (no reproducir). Curva: 1 en el centro → ~EDGE_GAIN en el borde (medio-viewport + 10),
   * cuadrática para que caiga rápido y "sentir" lejano lo de afuera de cámara.
   */
  public computeSpatial(tileX: number, tileY: number): { gain: number; pan: number } | null {
    const dx = tileX - this.listenerX;
    const dy = tileY - this.listenerY;
    const rangeX = this.viewHalfX + AUDIBLE_MARGIN_TILES;
    const rangeY = this.viewHalfY + AUDIBLE_MARGIN_TILES;
    // Distancia normalizada al viewport (elíptica): 0 en el centro, 1 en el borde del alcance.
    const nd = Math.sqrt((dx / rangeX) ** 2 + (dy / rangeY) ** 2);
    if (nd > 1) return null;
    const falloff = (1 - nd) ** 2; // 1 → 0
    const gain = EDGE_GAIN + (1 - EDGE_GAIN) * falloff;
    const pan = clamp(dx / rangeX, -1, 1);
    return { gain, pan };
  }

  // ==========================================================================
  // Carga (lazy, cacheada)
  // ==========================================================================

  private async loadPath(path: string): Promise<AudioBuffer | null> {
    if (this.buffers.has(path)) return this.buffers.get(path)!;
    if (this.missing.has(path)) return null;
    const inflight = this.loading.get(path);
    if (inflight) return inflight;

    const promise = this.fetchAndDecode(path);
    this.loading.set(path, promise);
    const buf = await promise;
    this.loading.delete(path);
    return buf;
  }

  private async fetchAndDecode(path: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    for (const ext of ['.ogg', '.mp3'] as const) {
      try {
        const res = await fetch(getAssetUrl(`audio/${path}${ext}`));
        if (!res.ok) continue;
        const arr = await res.arrayBuffer();
        const buf = await this.ctx.decodeAudioData(arr);
        this.buffers.set(path, buf);
        return buf;
      } catch {
        // probar siguiente extensión
      }
    }
    // No hay archivo cargado todavía: marcar ausente (no reintentar) y no-op silencioso.
    this.missing.add(path);
    return null;
  }

  /** Precarga un conjunto de ids (best-effort), incluyendo TODAS sus variantes. */
  public preload(ids: string[]): void {
    if (!this.ctx) return;
    for (const id of ids) {
      const def = SOUNDS[id];
      if (!def) continue;
      void this.loadPath(def.file);
      // Calentar también las variantes: si no, la 1ª vez que sale cada una decodifica async y la
      // variedad "arranca tarde" (se percibe como que no varían). Ver docs/SOUND_AND_MUSIC.md.
      def.variants?.forEach((v) => void this.loadPath(v));
    }
  }

  /**
   * Elige el path concreto de un sonido. Con `variantIndex` la elección es DETERMINISTA
   * (`index % nº variantes`); sin él, aleatoria. Sin variantes, siempre `def.file`.
   */
  private resolvePath(def: SoundDef, variantIndex?: number): string {
    if (def.variants && def.variants.length > 0) {
      const pool = [def.file, ...def.variants];
      const i =
        typeof variantIndex === 'number'
          ? ((variantIndex % pool.length) + pool.length) % pool.length
          : Math.floor(Math.random() * pool.length);
      return pool[i];
    }
    return def.file;
  }

  // ==========================================================================
  // Reproducción
  // ==========================================================================

  /** Reproduce un sonido por id. Devuelve un handle (o null si no aplica). */
  public play(id: string, opts: PlayOptions = {}): SoundHandle | null {
    const def = SOUNDS[id];
    if (!def || !this.ctx) return null;

    // Categoría apagada → no reproducir.
    const catState = this.config.categories[def.category];
    if (catState && !catState.enabled) return null;

    // Posicional: calcular gain/pan; fuera de alcance → no reproducir.
    let spatial: { gain: number; pan: number } | null = null;
    const positional =
      POSITIONAL_CATEGORIES.has(def.category) && typeof opts.tileX === 'number' && typeof opts.tileY === 'number';
    if (positional) {
      spatial = this.computeSpatial(opts.tileX!, opts.tileY!);
      if (!spatial) return null;
    }

    const path = this.resolvePath(def, opts.variantIndex);
    // Cargar si hace falta y reproducir cuando esté listo (async, fire-and-forget).
    const buffered = this.buffers.get(path);
    if (buffered) {
      return this.playBuffer(buffered, def, opts, spatial);
    }
    // Aún sin buffer: intentar cargar; si llega, reproducir one-shot (los loops se manejan aparte).
    void this.loadPath(path).then((buf) => {
      if (buf && !opts.loop && !def.loop) {
        this.playBuffer(buf, def, opts, spatial);
      }
    });
    return null;
  }

  /** Atajo posicional. */
  public playAt(id: string, tileX: number, tileY: number, opts: PlayOptions = {}): SoundHandle | null {
    return this.play(id, { ...opts, tileX, tileY });
  }

  private playBuffer(
    buffer: AudioBuffer,
    def: SoundDef,
    opts: PlayOptions,
    spatial: { gain: number; pan: number } | null
  ): SoundHandle | null {
    const ctx = this.ctx!;
    const bus = this.busGains.get(def.category);
    if (!bus) return null;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = opts.loop ?? def.loop ?? false;
    source.playbackRate.value = opts.rate ?? 1;

    const voice = ctx.createGain();
    const baseVol = (def.volume ?? 1) * (opts.volume ?? 1);
    voice.gain.value = baseVol * (spatial ? spatial.gain : 1);

    source.connect(voice);
    if (spatial) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = spatial.pan;
      voice.connect(panner);
      panner.connect(bus);
    } else {
      voice.connect(bus);
    }

    source.onended = (): void => {
      try {
        source.disconnect();
        voice.disconnect();
      } catch {
        /* noop */
      }
      opts.onEnd?.();
    };
    source.start();

    return {
      stop: (fadeMs?: number): void => {
        try {
          if (fadeMs && fadeMs > 0) {
            const now = ctx.currentTime;
            voice.gain.cancelScheduledValues(now);
            voice.gain.setValueAtTime(voice.gain.value, now);
            voice.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
            source.stop(now + fadeMs / 1000);
          } else {
            source.stop();
          }
        } catch {
          /* ya detenido */
        }
      },
    };
  }

  // ==========================================================================
  // Música (una pista a la vez, crossfade)
  // ==========================================================================

  public playMusic(id: string | null, crossfadeMs = 1200): void {
    if (!this.ctx) return;
    if (id === this.musicId) return;
    // Fade-out de la actual.
    this.stopMusic(crossfadeMs);
    if (!id) return;
    const def = SOUNDS[id];
    if (!def) return;
    const catState = this.config.categories[SoundCategory.MUSIC];
    if (catState && !catState.enabled) {
      this.musicId = id; // recordar para cuando se reactive
      return;
    }
    this.musicId = id;
    void this.loadPath(def.file).then((buf) => {
      if (!buf || this.musicId !== id || !this.ctx) return;
      const ctx = this.ctx;
      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.loop = true;
      source.playbackRate.value = this.musicRate;
      const gain = ctx.createGain();
      const target = def.volume ?? 1;
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(this.busGains.get(SoundCategory.MUSIC)!);
      source.start();
      const now = ctx.currentTime;
      gain.gain.linearRampToValueAtTime(target, now + crossfadeMs / 1000);
      this.musicSource = source;
      this.musicGain = gain;
    });
  }

  public stopMusic(fadeMs = 800): void {
    const src = this.musicSource;
    const gain = this.musicGain;
    this.musicSource = null;
    this.musicGain = null;
    this.musicId = null;
    if (!src || !gain || !this.ctx) return;
    const now = this.ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
      src.stop(now + fadeMs / 1000);
    } catch {
      /* noop */
    }
  }

  /** Acelera/normaliza la música actual (vida baja). Rampa suave. */
  public setMusicRate(rate: number): void {
    this.musicRate = rate;
    if (this.musicSource && this.ctx) {
      const now = this.ctx.currentTime;
      this.musicSource.playbackRate.cancelScheduledValues(now);
      this.musicSource.playbackRate.setValueAtTime(this.musicSource.playbackRate.value, now);
      this.musicSource.playbackRate.linearRampToValueAtTime(rate, now + 0.6);
    }
  }

  public getCurrentMusic(): string | null {
    return this.musicId;
  }

  // ==========================================================================
  // Loops gestionados (heartbeat, ambiente)
  // ==========================================================================

  /**
   * Arranca un loop gestionado por `id` (heartbeat, ambiente de zona, ítem único en el piso). Si se
   * pasan `tileX/tileY` y la categoría es posicional, el loop se atenúa/panea según la distancia al
   * oyente y puede actualizarse por frame con `updateLoopSpatial`. `loopKey` permite varias
   * instancias del mismo sonido con keys distintas (ej. un loop por ítem único).
   */
  public startLoop(id: string, opts: PlayOptions = {}, loopKey?: string): void {
    const key = loopKey ?? id;
    if (!this.ctx || this.loops.has(key)) return;
    const def = SOUNDS[id];
    if (!def) return;
    const catState = this.config.categories[def.category];
    if (catState && !catState.enabled) return;
    const positional =
      POSITIONAL_CATEGORIES.has(def.category) &&
      typeof opts.tileX === 'number' &&
      typeof opts.tileY === 'number';
    void this.loadPath(def.file).then((buf) => {
      if (!buf || !this.ctx || this.loops.has(key)) return;
      const ctx = this.ctx;
      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.loop = true;
      source.playbackRate.value = opts.rate ?? 1;
      const gain = ctx.createGain();
      const baseVolume = (def.volume ?? 1) * (opts.volume ?? 1);
      const bus = this.busGains.get(def.category)!;
      let panner: StereoPannerNode | undefined;
      if (positional) {
        const spatial = this.computeSpatial(opts.tileX!, opts.tileY!);
        gain.gain.value = baseVolume * (spatial?.gain ?? 0);
        panner = ctx.createStereoPanner();
        panner.pan.value = spatial?.pan ?? 0;
        source.connect(gain);
        gain.connect(panner);
        panner.connect(bus);
      } else {
        gain.gain.value = baseVolume;
        source.connect(gain);
        gain.connect(bus);
      }
      source.start();
      this.loops.set(key, {
        source,
        gain,
        panner,
        baseVolume,
        tileX: positional ? opts.tileX : undefined,
        tileY: positional ? opts.tileY : undefined,
      });
    });
  }

  /** Recalcula gain/pan de un loop posicional (llamar por frame mientras el oyente se mueve). */
  public updateLoopSpatial(loopKey: string): void {
    const loop = this.loops.get(loopKey);
    if (!loop || !loop.panner || typeof loop.tileX !== 'number' || typeof loop.tileY !== 'number') {
      return;
    }
    const spatial = this.computeSpatial(loop.tileX, loop.tileY);
    loop.gain.gain.value = loop.baseVolume * (spatial?.gain ?? 0);
    loop.panner.pan.value = spatial?.pan ?? 0;
  }

  /** Ids de loops activos (para refrescar su spatial). */
  public activeLoopKeys(): string[] {
    return Array.from(this.loops.keys());
  }

  public stopLoop(id: string, fadeMs = 300): void {
    const loop = this.loops.get(id);
    if (loop) this.stopLoopInternal(id, loop, fadeMs);
  }

  private stopLoopInternal(
    id: string,
    loop: { source: AudioBufferSourceNode; gain: GainNode },
    fadeMs = 300
  ): void {
    this.loops.delete(id);
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    try {
      loop.gain.gain.cancelScheduledValues(now);
      loop.gain.gain.setValueAtTime(loop.gain.gain.value, now);
      loop.gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
      loop.source.stop(now + fadeMs / 1000);
    } catch {
      /* noop */
    }
  }

  public isLoopActive(id: string): boolean {
    return this.loops.has(id);
  }

  // ==========================================================================
  // Filtro "muerte" + silencio
  // ==========================================================================

  /** Enmudece los buses posicionales con un low-pass ("burbuja") al estar muerto. */
  public setDeathMuffle(on: boolean): void {
    if (this.deathMuffled === on || !this.ctx) {
      this.deathMuffled = on;
      return;
    }
    this.deathMuffled = on;
    const now = this.ctx.currentTime;
    const target = on ? DEATH_FILTER_FREQ_MUFFLED : DEATH_FILTER_FREQ_OPEN;
    this.deathFilter.frequency.cancelScheduledValues(now);
    this.deathFilter.frequency.setValueAtTime(this.deathFilter.frequency.value, now);
    this.deathFilter.frequency.exponentialRampToValueAtTime(Math.max(1, target), now + 0.5);
  }

  /**
   * Reverb de "cueva" por zona. `intensity` 0..1 (0 = seco; 1 = wet máximo `REVERB_WET_MAX`). Rampa
   * el mix wet; idempotente si el objetivo no cambió. Lo llama `AudioWorldController` al entrar/salir
   * de un área con `reverbIntensity`. Ver docs/SOUND_AND_MUSIC.md.
   */
  public setReverb(intensity: number, rampMs = 600): void {
    const target = clamp01(intensity) * REVERB_WET_MAX;
    if (Math.abs(target - this.reverbTarget) < 0.001) return;
    this.reverbTarget = target;
    if (!this.ctx || !this.reverbWet) return;
    const now = this.ctx.currentTime;
    this.reverbWet.gain.cancelScheduledValues(now);
    this.reverbWet.gain.setValueAtTime(this.reverbWet.gain.value, now);
    this.reverbWet.gain.linearRampToValueAtTime(target, now + rampMs / 1000);
  }

  /**
   * Genera un impulse response procedural (sin asset) para el reverb: ruido estéreo con decaimiento
   * exponencial → cola tipo "cueva". `seconds` = largo de la cola; `decay` = curva de caída.
   */
  private makeImpulseResponse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const rate = ctx.sampleRate;
    const len = Math.max(1, Math.floor(seconds * rate));
    const buf = ctx.createBuffer(2, len, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
      }
    }
    return buf;
  }

  /** Fade global del duck (silencio del jugador local). target 0 = mudo, 1 = normal. */
  public fadeAll(target: number, ms: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.duckGain.gain.cancelScheduledValues(now);
    this.duckGain.gain.setValueAtTime(this.duckGain.gain.value, now);
    this.duckGain.gain.linearRampToValueAtTime(clamp01(target), now + ms / 1000);
  }

  /** Corta todo (al salir de la escena). */
  public shutdown(): void {
    this.stopMusic(0);
    for (const [id, loop] of this.loops) this.stopLoopInternal(id, loop, 0);
    if (this.ctx) void this.ctx.close();
    this.ctx = null;
    this.buffers.clear();
    this.loading.clear();
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function clamp01(v: number): number {
  return clamp(v, 0, 1);
}
